import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3200';

function authHeaders(): Record<string, string> {
  const token = JSON.parse(localStorage.getItem('urs-auth') ?? '{}')?.state?.token ?? '';
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

interface TenantSettings {
  id: string;
  name: string;
  slug: string;
  subscriptionTier: string;
  subscriptionStatus: string;
  trialEndsAt: string | null;
  billingEmail: string | null;
  billingPhone: string | null;
  country: string;
  currency: string;
  timezone: string;
  efrisTin: string | null;
  nssfNumber: string | null;
  lstMode: string;
  lstLumpSumMonth: number;
}

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const TIMEZONES = [
  'Africa/Kampala',
  'Africa/Nairobi',
  'Africa/Dar_es_Salaam',
  'Africa/Kigali',
  'Africa/Addis_Ababa',
  'UTC',
];

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 44, height: 24, borderRadius: 99, border: 'none', cursor: 'pointer',
        background: checked ? 'var(--accent)' : 'var(--surface2)',
        position: 'relative', flexShrink: 0, transition: 'background 0.2s',
        outline: 'none',
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: checked ? 23 : 3, width: 18, height: 18,
        borderRadius: '50%', background: checked ? '#fff' : 'var(--text-3)',
        transition: 'left 0.2s',
      }} />
    </button>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 style={{ fontSize: 15, marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid var(--border)', color: 'var(--text)' }}>
      {children}
    </h2>
  );
}

export function SettingsPage() {
  const { isDark, toggleTheme } = useTheme();
  const { showToast } = useToast();
  const qc = useQueryClient();

  const { data: settings, isLoading } = useQuery<TenantSettings>({
    queryKey: ['tenant-settings'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/admin/settings`, { headers: authHeaders() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed to load settings');
      return j.data as TenantSettings;
    },
  });

  const [form, setForm] = useState({
    name: '',
    billingEmail: '',
    billingPhone: '',
    timezone: 'Africa/Kampala',
    efrisTin: '',
    nssfNumber: '',
    lstMode: 'lump_sum',
    lstLumpSumMonth: 7,
  });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm({
        name: settings.name ?? '',
        billingEmail: settings.billingEmail ?? '',
        billingPhone: settings.billingPhone ?? '',
        timezone: settings.timezone ?? 'Africa/Kampala',
        efrisTin: settings.efrisTin ?? '',
        nssfNumber: settings.nssfNumber ?? '',
        lstMode: settings.lstMode ?? 'lump_sum',
        lstLumpSumMonth: settings.lstLumpSumMonth ?? 7,
      });
      setDirty(false);
    }
  }, [settings]);

  const updateMut = useMutation({
    mutationFn: async (payload: typeof form) => {
      const res = await fetch(`${API_BASE}/v1/admin/settings`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({
          name: payload.name || undefined,
          billingEmail: payload.billingEmail || undefined,
          billingPhone: payload.billingPhone || undefined,
          timezone: payload.timezone || undefined,
          efrisTin: payload.efrisTin || undefined,
          nssfNumber: payload.nssfNumber || undefined,
          lstMode: payload.lstMode || undefined,
          lstLumpSumMonth: payload.lstLumpSumMonth,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed to save settings');
      return j.data;
    },
    onSuccess: () => {
      showToast('Settings saved', 'success');
      setDirty(false);
      void qc.invalidateQueries({ queryKey: ['tenant-settings'] });
    },
    onError: (e: Error) => showToast(e.message, 'error'),
  });

  function setField(k: keyof typeof form, v: string | number) {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  }

  // Notifications prefs — stored in localStorage only (no backend model)
  const [notifPrefs, setNotifPrefs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('urs-notif-prefs') ?? '{}') as Record<string, boolean>; }
    catch { return {}; }
  });

  function setNotif(key: string, val: boolean) {
    const updated = { ...notifPrefs, [key]: val };
    setNotifPrefs(updated);
    localStorage.setItem('urs-notif-prefs', JSON.stringify(updated));
  }

  const [backupLoading, setBackupLoading] = useState(false);

  async function downloadBackup() {
    setBackupLoading(true);
    try {
      const res = await fetch(`${API_BASE}/v1/admin/backup`, { headers: authHeaders() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Backup failed');
      const blob = new Blob([JSON.stringify(j.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `restaurant-backup-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('Backup downloaded', 'success');
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setBackupLoading(false);
    }
  }

  const tierLabel: Record<string, string> = {
    casual: 'Casual / Street Food',
    standard: 'Standard',
    fullservice: 'Full Service',
    enterprise: 'Enterprise',
  };

  const statusBadge: Record<string, string> = {
    trial: 'badge badge-amber',
    active: 'badge badge-green',
    suspended: 'badge badge-red',
    cancelled: 'badge badge-gray',
  };

  return (
    <div>
      <div className="page-header">
        {dirty && (
          <button
            className="btn btn-primary"
            onClick={() => updateMut.mutate(form)}
            disabled={updateMut.isPending}
          >
            {updateMut.isPending ? 'Saving…' : 'Save Changes'}
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>

        {/* ── LEFT COLUMN ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Restaurant */}
          <div className="card">
            <SectionTitle>Restaurant</SectionTitle>
            {isLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[200, 200, 160, 160].map((w, i) => (
                  <div key={i} style={{ height: 38, borderRadius: 8, background: 'var(--surface2)', width: w }} />
                ))}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 14 }}>
                <div className="field">
                  <label className="field-label">Restaurant Name</label>
                  <input className="input" value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Restaurant name" />
                </div>
                <div className="field">
                  <label className="field-label">Billing Email</label>
                  <input className="input" type="email" value={form.billingEmail} onChange={(e) => setField('billingEmail', e.target.value)} placeholder="billing@restaurant.ug" />
                </div>
                <div className="field">
                  <label className="field-label">Billing Phone</label>
                  <input className="input" value={form.billingPhone} onChange={(e) => setField('billingPhone', e.target.value)} placeholder="+256 7XX XXX XXX" />
                </div>
                <div className="field">
                  <label className="field-label">Timezone</label>
                  <select
                    className="input"
                    value={form.timezone}
                    onChange={(e) => setField('timezone', e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Uganda Compliance */}
          <div className="card">
            <SectionTitle>Uganda Compliance</SectionTitle>
            {isLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[200, 200].map((w, i) => (
                  <div key={i} style={{ height: 38, borderRadius: 8, background: 'var(--surface2)', width: w }} />
                ))}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 14 }}>
                <div className="field">
                  <label className="field-label">EFRIS TIN (URA Tax ID)</label>
                  <input className="input" value={form.efrisTin} onChange={(e) => setField('efrisTin', e.target.value)} placeholder="e.g. 1000000000" />
                </div>
                <div className="field">
                  <label className="field-label">NSSF Employer Number</label>
                  <input className="input" value={form.nssfNumber} onChange={(e) => setField('nssfNumber', e.target.value)} placeholder="e.g. NSF-0000000" />
                </div>
              </div>
            )}
          </div>

          {/* Local Service Tax */}
          <div className="card">
            <SectionTitle>Local Service Tax (LST)</SectionTitle>
            {isLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[200, 160].map((w, i) => (
                  <div key={i} style={{ height: 38, borderRadius: 8, background: 'var(--surface2)', width: w }} />
                ))}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 14 }}>
                <div className="field">
                  <label className="field-label">Collection Mode</label>
                  <select
                    className="input"
                    value={form.lstMode}
                    onChange={(e) => setField('lstMode', e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="lump_sum">Lump Sum — full annual amount in one chosen month</option>
                    <option value="monthly">Monthly — spread across every payroll run (annual ÷ 12)</option>
                  </select>
                  <p style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                    Uganda law: LST is collected July–October. Lump sum in one month is the standard practice.
                  </p>
                </div>
                {form.lstMode === 'lump_sum' && (
                  <div className="field">
                    <label className="field-label">Collection Month</label>
                    <select
                      className="input"
                      value={form.lstLumpSumMonth}
                      onChange={(e) => setField('lstLumpSumMonth', Number(e.target.value))}
                      style={{ cursor: 'pointer' }}
                    >
                      {MONTHS.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                    <p style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                      LST will only be deducted in the payroll run for this month. All other months: LST = 0.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>

        {/* ── RIGHT COLUMN ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Subscription */}
          <div className="card">
            <SectionTitle>Subscription</SectionTitle>
            {settings ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
                    {tierLabel[settings.subscriptionTier] ?? settings.subscriptionTier}
                  </p>
                  <span className={statusBadge[settings.subscriptionStatus] ?? 'badge badge-gray'}>
                    {settings.subscriptionStatus}
                  </span>
                </div>
                {settings.trialEndsAt && (
                  <p style={{ fontSize: 12, color: 'var(--text-3)' }}>
                    Trial ends {new Date(settings.trialEndsAt).toLocaleDateString('en-UG')}
                  </p>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 4 }}>
                  <div style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--surface2)' }}>
                    <p style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Country</p>
                    <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{settings.country}</p>
                  </div>
                  <div style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--surface2)' }}>
                    <p style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Currency</p>
                    <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{settings.currency}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ height: 80, borderRadius: 8, background: 'var(--surface2)' }} />
            )}
          </div>

          {/* Appearance */}
          <div className="card">
            <SectionTitle>Appearance</SectionTitle>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>Theme</p>
                <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3 }}>
                  Currently using <strong style={{ color: 'var(--text-2)' }}>{isDark ? 'dark' : 'light'}</strong> mode
                </p>
              </div>
              <Toggle checked={isDark} onChange={toggleTheme} />
            </div>
          </div>

          {/* Notifications */}
          <div className="card">
            <SectionTitle>Notifications</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                { key: 'sms_low_stock', label: 'Low Stock Alerts', sub: 'SMS when inventory falls below reorder level' },
                { key: 'sms_large_order', label: 'Large Order Alerts', sub: 'SMS for orders above UGX 500,000' },
                { key: 'email_daily_report', label: 'Daily Summary Email', sub: 'End-of-day revenue and order summary' },
                { key: 'email_payroll', label: 'Payroll Reminders', sub: 'Email reminder before payroll processing date' },
                { key: 'sms_reservation', label: 'Reservation Confirmations', sub: 'Auto-SMS to guests when reservation is confirmed' },
              ].map(({ key, label, sub }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{label}</p>
                    <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{sub}</p>
                  </div>
                  <Toggle
                    checked={notifPrefs[key] ?? false}
                    onChange={(v) => setNotif(key, v)}
                  />
                </div>
              ))}
            </div>
            <p style={{ marginTop: 16, fontSize: 11, color: 'var(--text-3)' }}>
              SMS via Africa's Talking Uganda · Email via configured SMTP
            </p>
          </div>

          {/* Backup & Restore */}
          <div className="card">
            <SectionTitle>Backup & Restore</SectionTitle>
            <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 16, lineHeight: 1.5 }}>
              Export all your restaurant data as a JSON file — menus, inventory, employees, customers, and recent orders.
              Store the file securely as a backup.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                className="btn btn-primary"
                onClick={() => void downloadBackup()}
                disabled={backupLoading}
              >
                {backupLoading ? 'Preparing…' : 'Download Backup'}
              </button>
              <div style={{ padding: '8px 14px', borderRadius: 8, background: 'var(--surface2)', fontSize: 12, color: 'var(--text-3)', display: 'flex', alignItems: 'center' }}>
                Restore via database import — contact support
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
